// src/UserInput/BusinessLookup.tsx
// Business search for the Business (not employer). Supports AU ABN and NZ NZBN.

import { useEffect, useState } from 'react';

import {
    convertAbnAndNameLookup,
    ILookupName,
    lookupAbn,
    lookupBusinessName,
} from '@/apiCalls/businessLookup';
import { Combobox, IComboboxOption } from '@/components/Combobox';
import {
    selectBusinessName,
    selectIndustryType,
    setBusiness,
} from '@/context/borrower';
import { selectIsNZ } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'BusinessLookup';

export const BusinessLookup = ({ showErrors, baseId }: IProps): JSX.Element => {
    // Redux selectors
    const businessName = useAppSelector(selectBusinessName);
    const industryType = useAppSelector(selectIndustryType);
    const isNZ = useAppSelector(selectIsNZ);
    const dispatch = useAppDispatch();

    // Local state
    const [query, setQuery] = useState<undefined | string>(businessName);
    const [options, setOptions] = useState<IComboboxOption[]>([]);
    const [data, setData] = useState<ILookupName[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;
        setQuery(value);
    }

    // Debounced search by name or direct numeric lookup
    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(async () => {
            try {
                const newOptions: IComboboxOption[] = [];

                // Early exit: avoid requests on undefined / very short input
                if (query === undefined || query.trim().length < 2) {
                    setOptions([]);
                    setData([]);
                    setLoading(false);
                    return;
                }

                const isDigits = /^\d+$/.test(query);
                const isAuAbn = !isNZ && query.length === 11 && isDigits;
                const isNzbn = isNZ && /^\d{13}$/.test(query); // NZBN is exactly 13 digits

                if (isAuAbn || isNzbn) {
                    // Direct ABN/NZBN lookup
                    const r = await lookupAbn(query);
                    if (!r) {
                        setOptions([]);
                        setData([]);
                        setLoading(false);
                        return;
                    }

                    newOptions.push({ id: 0, value: r.EntityName || r.Abn });

                    // Provide one item to data so handleSelect can use it
                    setData([
                        {
                            Abn: r.Abn,
                            AbnStatus: r.AbnStatus || 'Active',
                            isCurrent: true,
                            Name: r.EntityName || r.Abn,
                            NameType: 'Business Name',
                            Postcode: r.AddressPostcode || '',
                            Score: 100,
                            State: r.AddressState || '',
                        },
                    ]);

                    setOptions(newOptions);
                    setLoading(false);
                    return;
                }

                // Name-based search (AU/NZ). Guard to always get an array.
                let resp: ILookupName[] | undefined;
                try {
                    resp = await lookupBusinessName(query);
                } catch {
                    resp = [];
                }
                const lookup = Array.isArray(resp) ? resp : [];

                lookup.forEach((item, index) => {
                    const tail = item.Abn ? ` • ${item.Abn.slice(-4)}` : '';
                    newOptions.push({
                        id: index,
                        value: `${item.Name}${tail}`,
                    });
                });

                setOptions(newOptions);
                setData(lookup);
                setLoading(false);
            } catch {
                setOptions([]);
                setData([]);
                setLoading(false);
            }
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    }, [query, isNZ]);

    // When user selects an option from the combobox
    async function handleSelect(option: IComboboxOption): Promise<void> {
        const nameLookupResult = data?.[option.id];
        if (!nameLookupResult) return;

        const abnLookupResult = await lookupAbn(nameLookupResult.Abn);
        if (!abnLookupResult) return;

        const business = convertAbnAndNameLookup(
            nameLookupResult,
            abnLookupResult
        );
        // ABN/NZBN lookup doesn't include industry; copy from state into the result
        business.industry = industryType;

        dispatch(setBusiness(business));
    }

    return (
        // The exported testId was declared but never applied — BusinessLookup renders a
        // Combobox, and Combobox stamps its OWN id on the root, so nothing in the tree ever
        // carried 'BusinessLookup'. Wrapped locally rather than teaching Combobox to forward
        // an override, which would change a component a dozen other suites assert against.
        <div data-testid={testId}>
            <Combobox
                options={options}
                loading={loading}
                placeholder=''
                heading={'Business name'}
                onChange={handleChange}
                query={query ?? businessName ?? ''}
                onSelect={handleSelect}
                showErrors={showErrors && !businessName}
                baseId={baseId}
                tooltipContent={`Search for and select your business by name or ${
                    isNZ ? 'NZBN' : 'ABN'
                }.`}
            />
        </div>
    );
};
